"use client"

import type React from "react"
import {useState, useEffect} from "react"
import "./LocationsPage.css"
import {Button} from "@/components/ui/button"
import {Label} from "@/components/ui/label"
import {Input} from "@/components/ui/input"
import {Checkbox} from "@/components/ui/checkbox"
import LocationCard from "./Components/LocationCard"
import axiosInstance from "@/api/axios"

// Backend model interfaces
interface BinData {
    id: number
    unique_identifier: string
    is_garfill: boolean
    is_weight_scale: boolean
    last_emptied_date: string
    bin_location: string
}

interface BinLocationData {
    id: number
    bin: BinData
    bin_latitude: string | null
    bin_longitude: string | null
    timestamp: string
}

interface BackendLocationData {
    id: number
    location_name: string
    location_image_url: string
    location_description: string
    latitude: string | null
    longitude: string | null
    bin_locations: BinLocationData[]
}

// Frontend display interface
interface LocationData {
    id: number
    name: string
    imageUrl: string
    description?: string
    totalBins: number
    bins: string[]
}

const LOCAL_STORAGE_KEY = "locationsData"

const ALL_BINS = [
    "R0",
    "R1",
    "R2",
    "R3",
    "R4",
    "R5",
    "R6",
    "R7",
    "R8",
    "R9",
    "R10",
    "R11",
    "R12",
    "R13",
    "R14",
    "R15",
    "R16",
    "R17",
    "R04",
    "IST01",
    "IST03",
    "A0",
    "H1",
    "J5",
    "F17",
]

interface ConfirmationDialogProps {
    isOpen: boolean
    locationName: string | null
    onConfirm: (name: string) => void
    onCancel: () => void
}

// Transform backend data to frontend format
const transformLocationData = (backendLocation: BackendLocationData): LocationData => {
    const transformed = {
        id: backendLocation.id,
        name: backendLocation.location_name,
        imageUrl: backendLocation.location_image_url,
        description: backendLocation.location_description,
        totalBins: backendLocation.bin_locations.length,
        bins: backendLocation.bin_locations.map((bl) => bl.bin.unique_identifier),
    }

    console.log("🔍 Transformed location:", transformed)
    return transformed
}

// Helper function to convert dataURL to File
const dataURLtoFile = async (dataurl: string, filename: string): Promise<File> => {
    const arr = dataurl.split(",")
    const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg"
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], filename, {type: mime})
}

const getAvailableBins = (locations: LocationData[], excludeBins: string[] = []) => {
    const usedBins = locations.flatMap((loc) => loc.bins).filter((bin) => !excludeBins.includes(bin))
    return ALL_BINS.filter((bin) => !usedBins.includes(bin))
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({isOpen, locationName, onConfirm, onCancel}) => {
    if (!isOpen || !locationName) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-md p-6">
                <h2 className="text-lg font-semibold mb-4">Confirm Delete</h2>
                <p className="mb-4">Are you sure you want to delete "{locationName}"?</p>
                <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button variant="destructive" onClick={() => onConfirm(locationName)}>
                        Delete
                    </Button>
                </div>
            </div>
        </div>
    )
}

const LocationsPage = () => {
    const initialHardcodedLocations: LocationData[] = [
        {
            id: 1,
            name: "Cafeteria",
            imageUrl: "/locations_cafeteria.jpg",
            totalBins: 5,
            bins: ALL_BINS.slice(0, 5),
        },
        {
            id: 2,
            name: "Foyer",
            imageUrl: "/locations_foyer.jpg",
            totalBins: 5,
            bins: ALL_BINS.slice(5, 10),
        },
        {
            id: 3,
            name: "Event Hall",
            imageUrl: "/locations_event.jpg",
            totalBins: 6,
            bins: ALL_BINS.slice(10, 16),
        },
    ]

    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [locationsData, setLocationsData] = useState<LocationData[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [useBackend, setUseBackend] = useState(false)
    const [newLocation, setNewLocation] = useState<{
        name: string
        imageUrl: string
        description: string
        selectedBins: string[]
        imageChanged: boolean
    }>({
        name: "",
        imageUrl: "",
        description: "",
        selectedBins: [],
        imageChanged: false,
    })
    const [editingLocation, setEditingLocation] = useState<LocationData | null>(null)
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
    const [locationToDelete, setLocationToDelete] = useState<string | null>(null)

    // Fetch locations from backend or fallback to localStorage
    useEffect(() => {

        const testCSRFAndDebug = async () => {
            console.log("=== CSRF & Debug Test ===");

            try {
                // Step 1: Get CSRF token first
                console.log("1. Getting CSRF token...");
                const csrfResponse = await axiosInstance.get('csrf/');
                console.log("CSRF Response:", csrfResponse.data);

                // Check if token is now in cookies
                const tokenFromCookie = document.cookie
                    .split('; ')
                    .find(row => row.startsWith('csrftoken='))
                    ?.split('=')[1];
                console.log("Token in cookie:", tokenFromCookie);

                // Step 2: Test GET request to debug endpoint
                console.log("2. Testing GET debug endpoint...");
                const getResponse = await axiosInstance.get("debug-auth-csrf");
                console.log("GET Debug Response:", getResponse.data);

                // Step 3: Test POST request to debug endpoint
                console.log("3. Testing POST debug endpoint...");
                const postResponse = await axiosInstance.post("debug-auth-csrf", {
                    test_data: "Hello from frontend POST with CSRF",
                });
                console.log("POST Debug Response:", postResponse.data);

                console.log("✅ All tests passed!");

            } catch (err: unknown) {
                const error = err as any; // Type assertion for axios error
                console.error("❌ Test failed:", error.response?.data || error.message);

                // Enhanced error logging
                if (error.response) {
                    console.error("Status:", error.response.status);
                    console.error("Headers:", error.response.headers);
                    console.error("Data:", error.response.data);
                }
            }

            console.log("=== End CSRF & Debug Test ===");
        };


        const fetchLocations = async () => {
            setLoading(true)
            try {
                // Make sure your axios instance includes authentication headers
                const response = await axiosInstance.get("locations/")
                const backendData: any[] = response.data
                const transformedData = backendData.map(transformLocationData)
                setLocationsData(transformedData)
                setUseBackend(true)
                setError(null)
            } catch (error: any) {
                console.error("Error fetching locations from backend:", error)

                // UPDATED: Better error handling for different status codes
                if (error.response && error.response.status === 401) {
                    setError("Authentication required. Please log in to access locations.")
                    // TODO: Redirect to login page
                    // window.location.href = '/login';
                } else if (error.response && error.response.status === 403) {
                    setError("Access denied. You don't have permission to view locations.")
                } else if (error.response && error.response.status === 404) {
                    setError("Locations endpoint not found.")
                } else if (error.response && error.response.status === 400) {
                    const errorData = error.response.data
                    if (errorData.error && errorData.error.includes("account information")) {
                        setError("User account information not found. Please contact support.")
                    } else {
                        setError("Bad request. Please check your data.")
                    }
                } else {
                    setError("Backend not available. Using local storage.")
                }

                setUseBackend(false)

                // Fallback to localStorage (existing code)
                if (typeof window !== "undefined") {
                    const storedData = localStorage.getItem(LOCAL_STORAGE_KEY)
                    try {
                        const localData = storedData ? JSON.parse(storedData) : initialHardcodedLocations
                        setLocationsData(localData)
                    } catch (e) {
                        console.error("Error parsing stored locations data:", e)
                        setLocationsData(initialHardcodedLocations)
                    }
                } else {
                    setLocationsData(initialHardcodedLocations)
                }
            } finally {
                setLoading(false)
            }
        }

        testCSRFAndDebug()
        fetchLocations()
    }, [])

    // Sync with localStorage when not using backend
    useEffect(() => {
        if (!loading && !useBackend && typeof window !== "undefined") {
            try {
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(locationsData))
            } catch (e) {
                console.error("Failed to save to local storage:", e)
            }
        }
    }, [locationsData, loading, useBackend])

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value, files} = e.target
        if (name === "image") {
            const file = files?.[0]
            if (file) {
                const reader = new FileReader()
                reader.onloadend = () => {
                    setNewLocation((prev) => ({
                        ...prev,
                        imageUrl: reader.result as string,
                        imageChanged: true,
                    }))
                }
                reader.readAsDataURL(file)
            }
        } else {
            setNewLocation((prev) => ({
                ...prev,
                [name]: value,
            }))
        }
    }

    const handleBinSelect = (binName: string) => {
        setNewLocation((prev) => {
            let updatedBins
            if (prev.selectedBins.includes(binName)) {
                updatedBins = prev.selectedBins.filter((name) => name !== binName)
            } else {
                updatedBins = [...prev.selectedBins, binName]
            }
            return {
                ...prev,
                selectedBins: updatedBins,
            }
        })
    }

    const createLocationBackendFixed = async (
        locationData: typeof newLocation,
        axiosInstance: any
    ): Promise<any> => {
        console.log("🚀 Creating location with fixed implementation...")

        // Debug current state
        console.log("📊 Current cookies:", document.cookie)
        console.log("📝 Location data:", locationData)

        try {
            // STEP 1: Ensure we have a fresh CSRF token
            console.log("🔐 Ensuring fresh CSRF token...")
            const csrfResponse = await fetch("http://127.0.0.1:8000/api/csrf/", {
                method: "GET",
                credentials: "include",
            })

            if (!csrfResponse.ok) {
                throw new Error(`Failed to get CSRF token: ${csrfResponse.status}`)
            }

            const csrfData = await csrfResponse.json()
            console.log("✅ Fresh CSRF token obtained")

            // STEP 2: Prepare the request data
            const formData = new FormData()
            formData.append("location_name", locationData.name)
            formData.append("location_description", locationData.description || "")

            // Handle image upload properly
            if (locationData.imageUrl && locationData.imageUrl.startsWith("data:")) {
                console.log("📎 Processing image upload...")
                const file = await dataURLtoFile(locationData.imageUrl, "location.jpg")
                formData.append("location_image_url", file)
            } else if (locationData.imageUrl) {
                // If it's a URL, send it as is
                formData.append("location_image_url", locationData.imageUrl)
            }

            // STEP 3: Make the request with proper headers
            console.log("📤 Sending POST request...")
            const response = await fetch("http://127.0.0.1:8000/api/locations/", {
                method: "POST",
                credentials: "include",
                headers: {
                    "X-CSRFToken": csrfData.csrfToken,
                    // Don't set Content-Type for FormData - let browser handle it
                },
                body: formData,
            })

            console.log("📥 Response status:", response.status)

            if (response.ok) {
                const responseData = await response.json()
                console.log("✅ Location created successfully:", responseData)
                return responseData
            } else {
                const errorText = await response.text()
                console.error("❌ Location creation failed:", response.status, errorText)

                // Parse error details
                let errorData
                try {
                    errorData = JSON.parse(errorText)
                } catch {
                    errorData = {error: errorText}
                }

                // Handle specific error cases
                if (response.status === 403) {
                    throw new Error("Access denied. Please check your authentication and permissions.")
                } else if (response.status === 400) {
                    if (errorData.error && errorData.error.includes("account information")) {
                        throw new Error("User account information not found. Please contact support.")
                    } else if (errorData.error && errorData.field === "location_name") {
                        throw new Error(errorData.error)
                    } else {
                        throw new Error(`Validation failed: ${JSON.stringify(errorData)}`)
                    }
                } else {
                    throw new Error(`Request failed: ${response.status} - ${errorText}`)
                }
            }
        } catch (error) {
            console.error("❌ Create location error:", error)
            throw error
        }
    }

    const updateLocationDetailsOnly = async (
        locationId: number,
        locationData: typeof newLocation,
        originalLocation: LocationData,
    ) => {
        // Check what actually changed in location details
        const nameChanged = locationData.name !== originalLocation.name
        const descriptionChanged = locationData.description !== (originalLocation.description || "")
        const imageChanged = locationData.imageChanged

        // If nothing changed in the location details, skip the location update
        if (!nameChanged && !descriptionChanged && !imageChanged) {
            console.log("No location details changed, skipping location update")
            return {id: locationId} // Return minimal response to continue
        }

        const formData = new FormData()

        // Only send fields that actually changed
        if (nameChanged) {
            formData.append("location_name", locationData.name)
            console.log("Updating location name to:", locationData.name)
        }

        if (descriptionChanged) {
            formData.append("location_description", locationData.description || "")
            console.log("Updating location description to:", locationData.description)
        }

        // Only append image if it was actually changed
        if (imageChanged && locationData.imageUrl.startsWith("data:")) {
            const file = await dataURLtoFile(locationData.imageUrl, "location.jpg")
            formData.append("location_image_url", file)
            console.log("Updating location image")
        }

        try {
            const response = await axiosInstance.patch(`locations/${locationId}/`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            console.log("Location details updated successfully")
            return response.data
        } catch (error: any) {
            console.error("Update location error:", error.response?.data)

            // UPDATED: Handle account-specific error messages
            if (error.response && error.response.status === 401) {
                throw new Error("Authentication required. Please log in again.")
            }
            if (error.response && error.response.status === 403) {
                throw new Error("Access denied. You don't have permission to update this location.")
            }
            if (error.response && error.response.status === 404) {
                throw new Error("Location not found or access denied.")
            }
            if (error.response && error.response.status === 400) {
                const errorData = error.response.data
                if (errorData.error && errorData.field === "location_name") {
                    // Error message now mentions "your account"
                    throw new Error(errorData.error)
                }
                if (errorData.error && errorData.error.includes("account information")) {
                    throw new Error("User account information not found. Please contact support.")
                }
                if (errorData.details) {
                    throw new Error(`Validation failed: ${JSON.stringify(errorData.details)}`)
                }
            }
            throw error
        }
    }
    const updateBinsOnly = async (locationId: number, binIdentifiers: string[]) => {
        try {
            console.log("Updating bins for location", locationId, "to:", binIdentifiers)

            // Get all bins to map identifiers to IDs
            const [binsResponse, allBinsResponse] = await Promise.allSettled([
                // ✅ FIXED: Remove extra 'api/' since baseURL already includes it
                axiosInstance.get("available-bins/"),
                axiosInstance.get("get_bins/"),
            ])

            let availableBins = []
            let allBins = []

            if (binsResponse.status === "fulfilled") {
                availableBins = binsResponse.value.data.results || binsResponse.value.data
            }

            if (allBinsResponse.status === "fulfilled") {
                allBins = allBinsResponse.value.data
            } else {
                allBins = availableBins
            }

            const binIdMap = allBins.reduce((map: Record<string, number>, bin: BinData) => {
                map[bin.unique_identifier] = bin.id
                return map
            }, {})

            const binIds = binIdentifiers.map((identifier) => binIdMap[identifier]).filter(Boolean)

            // Use PUT to replace all bin assignments for this location
            // This endpoint ONLY handles bins, never touches location data
            // ✅ FIXED: Remove extra 'api/' since baseURL already includes it
            const response = await axiosInstance.put(`locations/${locationId}/bins/`, {
                bin_ids: binIds,
            })

            console.log("Bins updated successfully:", response.data)
            return response.data
        } catch (error: any) {
            if (error.response && error.response.status === 403) {
                throw new Error("Authentication required. Please log in again.")
            }
            console.error("Error updating bins:", error)
            throw error
        }
    }

    const handleAddLocation = async (e: React.FormEvent) => {
        e.preventDefault()

        // 🔍 DEBUG LOGS:
        console.log("=== HANDLE ADD LOCATION DEBUG ===")
        console.log("editingLocation:", editingLocation)
        console.log("editingLocation?.id:", editingLocation?.id)
        console.log("useBackend:", useBackend)
        console.log("Condition editingLocation?.id:", !!editingLocation?.id)
        console.log("=== END DEBUG ===")

        if (useBackend) {
            try {
                let locationData

                // ✅ FIXED: Use proper condition check
                if (editingLocation && editingLocation.id) {
                    // Update existing location
                    console.log("✅ GOING TO UPDATE BRANCH")
                    console.log("Updating existing location with ID:", editingLocation.id)

                    // Check what changed
                    const nameChanged = newLocation.name !== editingLocation.name
                    const descriptionChanged = newLocation.description !== (editingLocation.description || "")
                    const imageChanged = newLocation.imageChanged
                    const binsChanged =
                        JSON.stringify([...newLocation.selectedBins].sort()) !== JSON.stringify([...editingLocation.bins].sort())

                    console.log("Changes detected:", {nameChanged, descriptionChanged, imageChanged, binsChanged})

                    // Step 1: Update location details only if they changed
                    if (nameChanged || descriptionChanged || imageChanged) {
                        console.log("Updating location details...")
                        locationData = await updateLocationDetailsOnly(editingLocation.id, newLocation, editingLocation)
                    } else {
                        console.log("No location details changed, skipping location update")
                        locationData = {id: editingLocation.id}
                    }

                    // Step 2: Update bin assignments only if they changed
                    if (binsChanged) {
                        console.log("Updating bin assignments...")
                        await updateBinsOnly(editingLocation.id, newLocation.selectedBins)
                    } else {
                        console.log("No bin changes detected, skipping bin update")
                    }
                } else {
                    // Create new location - use POST request
                    console.log("❌ GOING TO CREATE BRANCH")
                    console.log("Creating new location")
                    locationData = await createLocationBackendFixed(newLocation, axiosInstance)

                    // Assign bins to new location
                    if (newLocation.selectedBins.length > 0) {
                        await updateBinsOnly(locationData.id, newLocation.selectedBins)
                    }
                }

                // Refresh data from backend
                // ✅ FIXED: Remove extra 'api/' since baseURL already includes it
                const refreshResponse = await axiosInstance.get("locations/")
                const backendData: BackendLocationData[] = refreshResponse.data
                setLocationsData(backendData.map(transformLocationData))

                // Reset form and close sidebar
                setNewLocation({
                    name: "",
                    imageUrl: "",
                    description: "",
                    selectedBins: [],
                    imageChanged: false,
                })
                setEditingLocation(null)
                setSidebarOpen(false)
                setError(null)
            } catch (error: any) {
                console.error("Error handling location:", error)
                setError(error.message || "Failed to save location")
            }
        } else {
            // Local storage handling
            const isDuplicateName = locationsData.some(
                (loc) =>
                    loc.name.toLowerCase() === newLocation.name.toLowerCase() &&
                    (!editingLocation || loc.name !== editingLocation.name),
            )

            if (isDuplicateName) {
                setError("A location with this name already exists.")
                return
            }

            const locationData: LocationData = {
                // ✅ FIXED: Always provide ID for local storage
                id: editingLocation?.id || Math.max(0, ...locationsData.map((l) => l.id)) + 1,
                name: newLocation.name,
                imageUrl: newLocation.imageUrl,
                description: newLocation.description,
                totalBins: newLocation.selectedBins.length,
                bins: newLocation.selectedBins,
            }

            if (editingLocation) {
                setLocationsData((prevLocations) =>
                    prevLocations.map((loc) => (loc.id === editingLocation.id ? locationData : loc)),
                )
            } else {
                setLocationsData((prevLocations) => [...prevLocations, locationData])
            }

            setNewLocation({
                name: "",
                imageUrl: "",
                description: "",
                selectedBins: [],
                imageChanged: false,
            })
            setEditingLocation(null)
            setSidebarOpen(false)
            setError(null)
        }
    }

    const handleEditLocation = (location: LocationData) => {
        console.log("🔍 Editing location:", location)
        console.log("🔍 Location ID:", location.id, typeof location.id)

        setEditingLocation(location)
        setNewLocation({
            name: location.name,
            imageUrl: location.imageUrl,
            description: location.description || "",
            selectedBins: [...location.bins], // Create a copy to avoid reference issues
            imageChanged: false,
        })
        setSidebarOpen(true)
        setError(null) // Clear any previous errors
    }

    const requestDeleteLocation = (locationName: string) => {
        setLocationToDelete(locationName)
        setDeleteConfirmationOpen(true)
    }

    const confirmDeleteLocation = async (nameToDelete: string) => {
        const locationToDelete = locationsData.find((loc) => loc.name === nameToDelete)

        if (useBackend && locationToDelete?.id) {
            try {
                await axiosInstance.delete(`locations/${locationToDelete.id}/`)

                // Refresh data from backend
                const refreshResponse = await axiosInstance.get("locations/")
                const backendData: BackendLocationData[] = refreshResponse.data
                setLocationsData(backendData.map(transformLocationData))
            } catch (error: any) {
                console.error("Error deleting location from backend:", error)

                // UPDATED: Handle account-specific error messages
                if (error.response && error.response.status === 401) {
                    setError("Authentication required. Please log in again.")
                } else if (error.response && error.response.status === 403) {
                    setError("Access denied. You don't have permission to delete this location.")
                } else if (error.response && error.response.status === 404) {
                    setError("Location not found or access denied.")
                } else if (error.response && error.response.status === 400) {
                    const errorData = error.response.data
                    if (errorData.error && errorData.error.includes("account information")) {
                        setError("User account information not found. Please contact support.")
                    } else {
                        setError("Failed to delete location from backend. Using local storage.")
                    }
                } else {
                    setError("Failed to delete location from backend. Using local storage.")
                }
                setUseBackend(false)
            }
        }

        if (!useBackend) {
            // Local storage fallback
            setLocationsData((prevLocations) => prevLocations.filter((loc) => loc.name !== nameToDelete))
        }

        setDeleteConfirmationOpen(false)
        setLocationToDelete(null)
    }


    const cancelDeleteLocation = () => {
        setDeleteConfirmationOpen(false)
        setLocationToDelete(null)
    }

    // Check for duplicate names only when creating new locations or changing existing names
    const isDuplicateName = locationsData.some(
        (loc) =>
            loc.name.toLowerCase() === newLocation.name.toLowerCase() &&
            (!editingLocation || loc.name !== editingLocation.name),
    )

    // Check what has changed for visual feedback
    const hasChanges = editingLocation
        ? newLocation.name !== editingLocation.name ||
        newLocation.description !== (editingLocation.description || "") ||
        newLocation.imageChanged ||
        JSON.stringify([...newLocation.selectedBins].sort()) !== JSON.stringify([...editingLocation.bins].sort())
        : true

    return (
        <div className="relative">
            <ConfirmationDialog
                isOpen={deleteConfirmationOpen}
                locationName={locationToDelete}
                onConfirm={confirmDeleteLocation}
                onCancel={cancelDeleteLocation}
            />

            <div className="p-6 flex justify-end mb-6">
                <Button
                    onClick={() => {
                        setSidebarOpen(true)
                        setEditingLocation(null)
                        setNewLocation({
                            name: "",
                            imageUrl: "",
                            description: "",
                            selectedBins: [],
                            imageChanged: false,
                        })
                        setError(null)
                    }}
                >
                    + Add New Location
                </Button>
            </div>

            <div className="w-full max-w-[1920px] p-4 m-auto pt-0">
                {loading ? (
                    <div className="flex justify-center items-center h-40">
                        <p>Loading locations...</p>
                    </div>
                ) : error ? (
                    <div className="bg-yellow-50 p-4 rounded-md mb-4">
                        <p className="text-yellow-700">{error}</p>
                    </div>
                ) : null}

                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-4 sm:pt-0">
                    {locationsData.map((location) => (
                        <LocationCard
                            key={location.id}
                            id={location.id}
                            name={location.name}
                            imageUrl={location.imageUrl}
                            description={location.description}
                            totalBins={location.totalBins}
                            bins={location.bins}
                            onEdit={handleEditLocation}
                            onDelete={() => requestDeleteLocation(location.name)}
                        />
                    ))}
                    {locationsData.length === 0 && (
                        <div className="col-span-full text-center py-8">
                            <p className="text-slate-500">No locations added yet.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Sidebar form */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-40 flex justify-end">
                    <div className="fixed inset-0 bg-black bg-opacity-30" onClick={() => setSidebarOpen(false)}/>
                    <div
                        className="relative bg-white w-full max-w-md p-6 shadow-lg transform transition-transform duration-300">
                        <h2 className="text-xl font-semibold mb-4">
                            {editingLocation ? `Edit Location: ${editingLocation.name}` : "Add New Location"}
                        </h2>
                        <form onSubmit={handleAddLocation}>
                            <div className="mb-4">
                                <Label htmlFor="location-name" className="block mb-1 font-medium">
                                    Location Name
                                </Label>
                                <Input
                                    type="text"
                                    id="location-name"
                                    name="name"
                                    value={newLocation.name}
                                    onChange={handleFormChange}
                                    className="w-full border rounded p-2"
                                    required
                                />
                                {isDuplicateName && (
                                    <p className="text-sm text-red-500 mt-1">A location with this name already
                                        exists.</p>
                                )}
                            </div>

                            <div className="mb-4">
                                <Label htmlFor="location-description" className="block mb-1 font-medium">
                                    Description
                                </Label>
                                <Input
                                    type="text"
                                    id="location-description"
                                    name="description"
                                    value={newLocation.description}
                                    onChange={handleFormChange}
                                    className="w-full border rounded p-2"
                                />
                            </div>

                            <div className="mb-4">
                                <Label htmlFor="upload-image" className="block mb-1 font-medium">
                                    Upload Image
                                </Label>
                                <Input
                                    type="file"
                                    id="upload-image"
                                    name="image"
                                    accept="image/*"
                                    onChange={handleFormChange}
                                    className="w-full border rounded p-2"
                                />
                                {editingLocation?.imageUrl && !newLocation.imageChanged && (
                                    <div className="mt-2">
                                        <img
                                            src={editingLocation.imageUrl || "/placeholder.svg"}
                                            alt="Current Location Image"
                                            className="max-h-20 object-contain rounded"
                                        />
                                        <p className="text-sm text-gray-500 mt-1">Current image</p>
                                    </div>
                                )}
                                {newLocation.imageChanged && newLocation.imageUrl && (
                                    <div className="mt-2">
                                        <img
                                            src={newLocation.imageUrl || "/placeholder.svg"}
                                            alt="New Location Preview"
                                            className="max-h-20 object-contain rounded"
                                        />
                                        <p className="text-sm text-green-600 mt-1">New image selected</p>
                                    </div>
                                )}
                            </div>

                            <div className="mb-4">
                                <Label className="block mb-1 font-medium">Select Bins</Label>
                                <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto border rounded p-2">
                                    {getAvailableBins(locationsData, editingLocation?.bins || []).map((binName) => (
                                        <div key={binName} className="flex items-center">
                                            <Checkbox
                                                id={`bin-${binName}`}
                                                checked={newLocation.selectedBins.includes(binName)}
                                                onCheckedChange={() => handleBinSelect(binName)}
                                            />
                                            <Label htmlFor={`bin-${binName}`} className="ml-2 text-sm">
                                                {binName}
                                            </Label>
                                        </div>
                                    ))}
                                </div>
                                {editingLocation && (
                                    <div className="mt-2 text-sm">
                                        <p className="text-gray-600">
                                            Original
                                            bins: {editingLocation.bins.length > 0 ? editingLocation.bins.join(", ") : "None"}
                                        </p>
                                        <p className="text-blue-600">
                                            Selected bins:{" "}
                                            {newLocation.selectedBins.length > 0 ? newLocation.selectedBins.join(", ") : "None"}
                                        </p>
                                        {JSON.stringify([...newLocation.selectedBins].sort()) !==
                                            JSON.stringify([...editingLocation.bins].sort()) && (
                                                <p className="text-orange-600 font-medium">⚠️ Bin assignments will be
                                                    updated</p>
                                            )}
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button type="button" variant="secondary" onClick={() => setSidebarOpen(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isDuplicateName || !hasChanges}
                                    className={!hasChanges ? "opacity-50" : ""}
                                >
                                    {editingLocation ? "Save Changes" : "Add Location"}
                                    {editingLocation && !hasChanges && " (No Changes)"}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

export default LocationsPage
